import type { QueryRowType } from "@rocicorp/zero";
import { styled } from "goober";
import { useMemo } from "react";
import { queries, roundedHoursAgo } from "../../shared/queries";
import { isRouteType } from "../../shared/stepConfig";
import { config } from "../environment";
import { useQuery } from "../hooks/useQuery";
import { useSpaceDisplayName } from "../hooks/useSpaceDisplayName";
import { isSpaceMember, useSpaces } from "../hooks/useSpaces";
import { mainBranchConnectorIds } from "../lib/connector";
import { navigate, routes, useCurrentUrl } from "../lib/urls";
import { userDisplayName } from "../lib/userDisplayName";
import { Backdrop } from "./Backdrop";
import { parseMultiFilter } from "./ListFilter";
import { MainScrollContainer } from "./MainScrollContainer";
import { countPageCards, PagesList } from "./PagesList";
import {
  HeaderActions,
  HeaderCountBadge,
  SearchField,
} from "./Settings/shared";
import { TabHeaderBar } from "./TabHeader";
import { Text } from "./Text";
import { workflowListFilters } from "./workflowListFilters";

const Container = styled("div")`
  flex: 1;
  background: var(--surface-primary);
  outline: 0.5px solid var(--border-alpha);
  border-radius: 16px;
  box-shadow: var(--elevation-low);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const Heading = styled("div")`
  display: flex;
  align-items: center;
  height: 56px;
  padding: 0 24px;
`;

type LinkWorkflow = QueryRowType<
  ReturnType<typeof queries.allWorkflowsWithLinks>
>;
type LinkSpace = NonNullable<LinkWorkflow["space"]>;
type LinkCreator = NonNullable<LinkWorkflow["createdBy"]>;
type ConnectorInfo = QueryRowType<ReturnType<typeof queries.connectorsIndex>>;

// Sentinel space-filter id for the bucket collapsing every space the viewer
// isn't a member of. Not a real space id, and not "all" (reserved by
// parseMultiFilter/toggleMultiFilter as the empty-selection marker).
const OTHER_SPACES_FILTER_ID = "__spaces_not_in__";

export function Links() {
  const activitySince = roundedHoursAgo(24);
  const [allConnectors] = useQuery(queries.connectorsIndex());
  const [workflows, workflowsResult] = useQuery(
    queries.allWorkflowsWithLinks({ activitySince })
  );
  const [routeFavoriteIds] = useQuery(queries.userFavoriteRouteIds());
  const routeFavoriteSet = useMemo(
    () => new Set(routeFavoriteIds.map((f) => `${f.workflowId}:${f.stepDir}`)),
    [routeFavoriteIds]
  );
  const displayName = useSpaceDisplayName();
  const [allSpaces] = useSpaces({ settingsView: false });
  const memberSpaceIds = useMemo(
    () => new Set(allSpaces.filter(isSpaceMember).map((s) => s.id)),
    [allSpaces]
  );

  const connectorById = useMemo(
    () => new Map(allConnectors.map((a) => [a.id, a])),
    [allConnectors]
  );

  const {
    type: typeParam,
    space: spaceParam,
    app: appParam,
    creator: creatorParam,
    q,
  } = useCurrentUrl(routes.links);
  const typeFilter = isRouteType(typeParam) ? typeParam : null;
  const spaceFilters = parseMultiFilter(spaceParam);
  const appFilters = parseMultiFilter(appParam);
  const creatorFilters = parseMultiFilter(creatorParam);
  const query = (q ?? "").trim().toLowerCase();

  const pagesRows = workflows
    .map((w) => ({ workflow: w, space: w.space }))
    .filter(
      (
        r
      ): r is {
        workflow: NonNullable<typeof r.workflow>;
        space: NonNullable<typeof r.space>;
      } =>
        r.workflow != null &&
        r.space != null &&
        r.workflow.branches.some((b) => (b.headCommit?.routes?.length ?? 0) > 0)
    );

  const availableSpaces = new Map<string, LinkSpace>();
  const availableAppIds = new Set<string>();
  const availableCreators = new Map<string, LinkCreator>();
  for (const { workflow, space } of pagesRows) {
    availableSpaces.set(space.id, space);
    for (const appId of mainBranchConnectorIds(workflow.branches)) {
      availableAppIds.add(appId);
    }
    if (workflow.createdByUserId && workflow.createdBy) {
      availableCreators.set(workflow.createdByUserId, workflow.createdBy);
    }
  }
  const availableSpaceOptions = [...availableSpaces.values()].sort((a, b) =>
    displayName(a).localeCompare(displayName(b))
  );
  const memberSpaceOptions = availableSpaceOptions.filter((s) =>
    memberSpaceIds.has(s.id)
  );
  const hasNonMemberSpaces = availableSpaceOptions.some(
    (s) => !memberSpaceIds.has(s.id)
  );
  const availableApps = [...availableAppIds]
    .map((id) => connectorById.get(id))
    .filter((a): a is ConnectorInfo => a != null)
    .sort((a, b) => a.name.localeCompare(b.name));
  const availableCreatorOptions = [...availableCreators.values()].sort((a, b) =>
    userDisplayName(a).localeCompare(userDisplayName(b))
  );

  const filteredRows = pagesRows.filter(({ workflow, space }) => {
    if (query) {
      const nameMatch = workflow.name.toLowerCase().includes(query);
      const routeMatch = workflow.branches.some(
        (b) =>
          b.name === "main" &&
          (b.headCommit?.routes ?? []).some(
            (r) =>
              r.route.toLowerCase().includes(query) ||
              r.stepDir.toLowerCase().includes(query)
          )
      );
      if (!nameMatch && !routeMatch) return false;
    }
    if (spaceFilters.length > 0) {
      const realIdMatch = spaceFilters.includes(space.id);
      const bucketMatch =
        spaceFilters.includes(OTHER_SPACES_FILTER_ID) &&
        !memberSpaceIds.has(space.id);
      if (!realIdMatch && !bucketMatch) return false;
    }
    if (appFilters.length > 0) {
      const ids = mainBranchConnectorIds(workflow.branches);
      if (!appFilters.some((id) => ids.includes(id))) return false;
    }
    if (creatorFilters.length > 0) {
      if (
        !workflow.createdByUserId ||
        !creatorFilters.includes(workflow.createdByUserId)
      )
        return false;
    }
    return true;
  });

  const filters = workflowListFilters({
    space: {
      options: memberSpaceOptions,
      values: spaceFilters,
      displayName,
      otherBucket: hasNonMemberSpaces
        ? { id: OTHER_SPACES_FILTER_ID, label: "Spaces you’re not in" }
        : undefined,
    },
    app: { options: availableApps, values: appFilters },
    creator: { options: availableCreatorOptions, values: creatorFilters },
  });

  const isComplete = workflowsResult.type === "complete";

  return (
    <>
      <title>{`Links • ${config.title}`}</title>
      <Backdrop permanentNavigation>
        <Container>
          <TabHeaderBar>
            <Heading>
              <Text variant="text-regular-md">Links</Text>
              {isComplete && (
                <HeaderCountBadge
                  count={countPageCards(filteredRows, {
                    typeFilter,
                    favFilter: false,
                    routeFavoriteSet,
                  })}
                />
              )}
            </Heading>
            <HeaderActions>
              <SearchField
                placeholder="Search links…"
                value={q ?? ""}
                onChange={(next) =>
                  navigate(null, { q: next }, { replace: true })
                }
                width={240}
              />
            </HeaderActions>
          </TabHeaderBar>
          <MainScrollContainer>
            <PagesList
              rows={filteredRows.map((r) => ({
                ...r,
                hideSpaceName: !memberSpaceIds.has(r.space.id),
              }))}
              linksRoute={routes.links}
              showSpaceColumn
              routeFavorites
              filters={filters}
              isComplete={isComplete}
              emptyTitle="No links yet"
              emptyDescription="Links you can access across your spaces will appear here."
            />
          </MainScrollContainer>
        </Container>
      </Backdrop>
    </>
  );
}
